import axios, { AxiosHeaders } from 'axios';
import type { ApiErrorEnvelope } from '@/types/api';

/**
 * A failure normalised into something the UI can branch on. `code` is the
 * backend `errorCode` — never the message, which is localized server-side and
 * may be reworded at any time.
 */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly fieldErrors: Record<string, string>;
  readonly path?: string;
  /** Seconds to wait, from the Retry-After header on a 429. Never below 1. */
  readonly retryAfterSeconds?: number;
  /**
   * The optional `details` map. Only the codes that document it carry one —
   * see `ApiErrorEnvelope`. Empty when the envelope had none.
   */
  readonly details: Record<string, string>;
  /**
   * The backend's id for the failed request: the envelope's own `requestId`
   * first (backend 628d9f9, on every error), the `X-Request-Id` header every
   * response carries (`RequestIdFilter`, exposed cross-origin since the same
   * change) when the body has none — a response that never reached the error
   * handler, or an older backend. It is what the 500 page shows so a person
   * can quote it to support and the log line can be found. Absent when no
   * response arrived.
   */
  readonly requestId?: string;

  constructor(
    envelope: Partial<ApiErrorEnvelope> & { errorCode: string; statusCode: number },
    retryAfterSeconds?: number,
    requestId?: string,
  ) {
    super(envelope.message ?? envelope.errorCode);
    this.name = 'ApiError';
    this.code = envelope.errorCode;
    this.status = envelope.statusCode;
    this.path = envelope.path;
    this.fieldErrors = Object.fromEntries(
      (envelope.fieldErrors ?? []).map((f) => [f.field, f.message]),
    );
    this.details = envelope.details ?? {};
    this.retryAfterSeconds = retryAfterSeconds;
    this.requestId = envelope.requestId || requestId;
  }

  get isValidation(): boolean {
    return this.code === 'VALIDATION_FAILED';
  }
}

export const NETWORK_ERROR = 'NETWORK_ERROR';

export function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error;
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as ApiErrorEnvelope | undefined;
    const retryAfter = Number(error.response?.headers?.['retry-after']);
    const headers = error.response?.headers;
    // Axios lower-cases the names it parses off a real response; an
    // `AxiosHeaders` built by hand keeps its case, so ask it case-blind.
    const header = headers instanceof AxiosHeaders ? headers.get('x-request-id') : headers?.['x-request-id'];
    const requestId = typeof header === 'string' && header ? header : undefined;
    if (data?.errorCode)
      return new ApiError(
        data,
        Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : undefined,
        requestId,
      );
    return new ApiError(
      {
        errorCode: NETWORK_ERROR,
        statusCode: error.response?.status ?? 0,
        message: error.message,
      },
      undefined,
      requestId,
    );
  }
  return new ApiError({
    errorCode: 'INTERNAL_ERROR',
    statusCode: 0,
    message: error instanceof Error ? error.message : String(error),
  });
}

/** Translation key for an error, falling back to a generic message. */
export function errorMessageKey(error: ApiError): string {
  if (error.code === NETWORK_ERROR) return 'errors.network';
  return `errors.${error.code}`;
}
